import { useState } from 'react';
import { Users } from 'lucide-react';

import { useUsers } from '../hooks/use-users';
import { useAuth } from '../providers/auth-provider';
import { Button } from './Button';
import { Input } from './Input';
import { UserInviteModal } from './UserInviteModal';
import { UserListItem } from './UserListItem';

import type { SortOption, UserRole, UserStatus } from '../lib/types';

const ROLE_CHIPS: { value: UserRole; label: string }[] = [
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'member', label: 'Member' },
];

const ROLE_CHIP_CLASSES = {
  active: 'bg-sd-info/20 text-sd-info border-sd-info/30 hover:bg-sd-info/25',
  inactive: 'bg-transparent text-sd-text-secondary border-sd-border/30 hover:bg-sd-surface-elevated/60 hover:border-sd-border/50',
};

const STATUS_CHIPS: { value: UserStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'deactivated', label: 'Deactivated' },
];

const STATUS_CHIP_CLASSES: Record<UserStatus, { active: string; inactive: string }> = {
  active: {
    active: 'bg-sd-success/20 text-sd-success border-sd-success/30 hover:bg-sd-success/25',
    inactive: 'bg-transparent text-sd-text-secondary border-sd-border/30 hover:bg-sd-surface-elevated/60 hover:border-sd-border/50',
  },
  deactivated: {
    active: 'bg-sd-error/20 text-sd-error border-sd-error/30 hover:bg-sd-error/25',
    inactive: 'bg-transparent text-sd-text-secondary border-sd-border/30 hover:bg-sd-surface-elevated/60 hover:border-sd-border/50',
  },
};

function UsersViewSkeleton() {
  return (
    <div data-testid="users-skeleton" className="flex flex-col h-full bg-sd-background animate-pulse">
      <div className="flex items-center justify-between px-sd-3 pt-sd-3 pb-sd-1">
        <div className="h-6 w-20 bg-sd-border/30 rounded-sd-md" />
        <div className="h-8 w-32 bg-sd-border/30 rounded-sd-md" />
      </div>
      <div className="p-sd-2">
        <div className="h-10 bg-sd-border/20 rounded-sd-md" />
      </div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-sd-2 px-sd-3 py-sd-3 border-b border-sd-border/20">
          <div className="w-8 h-8 rounded-sd-full bg-sd-border/30" />
          <div className="flex-1">
            <div className="h-4 w-32 bg-sd-border/30 rounded mb-1" />
            <div className="h-3 w-20 bg-sd-border/20 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function UsersView() {
  const {
    users,
    status,
    isRefreshing,
    error,
    roleCounts,
    filters,
    setFilters,
    reload,
    createUser,
    updateUser,
    deactivateUser,
  } = useUsers({ enabled: true });

  const { user } = useAuth();
  const actorRole = (user?.role ?? 'member') as UserRole;

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleInvite = async (data: { email: string; role: UserRole; display_name?: string | undefined }) => {
    setIsSaving(true);
    try {
      await createUser(data);
      setShowInviteModal(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangeRole = async (userId: string, newRole: UserRole) => {
    try {
      await updateUser(userId, { role: newRole });
    } catch { /* handled by hook */ }
  };

  const handleDeactivate = async (userId: string) => {
    try {
      await deactivateUser(userId);
    } catch { /* handled by hook */ }
  };

  if (status === 'loading') {
    return <UsersViewSkeleton />;
  }

  if (status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center h-full p-sd-4 text-center">
        <p className="text-sd-error mb-sd-3">{error ?? 'Failed to load users'}</p>
        <Button variant="primary" onClick={reload}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col h-full bg-sd-background">
      {isRefreshing && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-sd-warning/60 animate-pulse z-10" />
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-sd-3 pt-sd-3 pb-sd-1">
        <h2 className="text-sd-lg font-semibold text-sd-text">Team</h2>
        <Button variant="primary" size="sm" onClick={() => setShowInviteModal(true)}>+ Invite</Button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col gap-sd-2 p-sd-2 bg-sd-surface-elevated @md:flex-row @md:items-center shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <div className="flex flex-wrap gap-sd-1">
          {STATUS_CHIPS.map(({ value, label }) => {
            const isActive = filters.status === value;
            return (
              <span
                key={value}
                role="button"
                tabIndex={0}
                aria-label={`Filter by ${label}`}
                aria-pressed={isActive}
                className={`inline-flex items-center px-sd-2 py-sd-1 text-sd-sm font-medium border rounded-sd-full cursor-pointer select-none transition-all duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1 ${isActive ? STATUS_CHIP_CLASSES[value].active : STATUS_CHIP_CLASSES[value].inactive}`}
                onClick={() => setFilters({ ...filters, status: value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setFilters({ ...filters, status: value });
                  }
                }}
              >
                {label}
              </span>
            );
          })}
          <span className="mx-sd-1 w-px h-4 bg-sd-border/30 self-center" />
          {ROLE_CHIPS.map(({ value, label }) => {
            const isActive = filters.role === value;
            return (
              <span
                key={value}
                role="button"
                tabIndex={0}
                aria-label={`Filter by ${label}`}
                aria-pressed={isActive}
                className={`inline-flex items-center px-sd-2 py-sd-1 text-sd-sm font-medium border rounded-sd-full cursor-pointer select-none transition-all duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1 ${isActive ? ROLE_CHIP_CLASSES.active : ROLE_CHIP_CLASSES.inactive}`}
                onClick={() => setFilters({ ...filters, role: isActive ? null : value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setFilters({ ...filters, role: isActive ? null : value });
                  }
                }}
              >
                {label}
                {roleCounts[value] > 0 && (
                  <span className="ml-sd-1 text-sd-xs opacity-70">{roleCounts[value]}</span>
                )}
              </span>
            );
          })}
        </div>
        <Input
          id="user-search"
          variant="search"
          placeholder="Search users..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
        <select
          aria-label="Sort users by"
          value={filters.sortBy}
          onChange={(e) => setFilters({ ...filters, sortBy: e.target.value as SortOption })}
          className="bg-sd-surface-elevated border border-sd-border/30 rounded-sd-sm px-sd-2 py-sd-1 text-sd-sm text-sd-text focus:ring-2 focus:ring-sd-primary focus:outline-none transition-colors duration-150 motion-reduce:transition-none"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
        </select>
      </div>

      {/* Content */}
      {users.length === 0 ? (
        <div data-testid="users-empty" className="flex flex-col items-center justify-center flex-1 p-sd-4 text-center">
          <div className="w-12 h-12 rounded-sd-lg bg-sd-info/10 flex items-center justify-center mb-sd-3 sd-reveal-1">
            <Users size={24} className="text-sd-info/60" />
          </div>
          <p className="text-sd-lg font-semibold text-sd-text mb-sd-1 sd-reveal-2">
            {filters.status === 'deactivated' ? 'No deactivated users' : 'No team members yet'}
          </p>
          <p className="text-sd-sm text-sd-text-muted max-w-xs sd-reveal-3">
            {filters.status === 'deactivated'
              ? 'All team members are currently active.'
              : 'Invite team members to collaborate on this workspace.'}
          </p>
        </div>
      ) : (
        <ul role="list" aria-label="Team members" className="flex-1 overflow-y-auto">
          {users.map((u) => (
            <UserListItem
              key={u.id}
              id={u.id}
              email={u.email}
              displayName={u.display_name}
              role={u.role}
              createdAt={u.created_at}
              isDeactivated={u.deactivated_at !== null}
              actorRole={actorRole}
              onChangeRole={u.id !== user?.id ? (newRole) => { void handleChangeRole(u.id, newRole); } : undefined}
              onDeactivate={u.id !== user?.id ? () => { void handleDeactivate(u.id); } : undefined}
            />
          ))}
        </ul>
      )}

      {/* Invite Modal */}
      <UserInviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleInvite}
        actorRole={actorRole}
        isSaving={isSaving}
      />
    </div>
  );
}
